import ClientSuspense from '@components/ClientSuspense'
import classnames from '@helpers/misc/classnames'
import {getPath} from '@helpers/router/getPathname'
import useHasUserRoleInWebsite from '@hooks/useHasUserRoleInWebsite'
import useInitialData from '@page-components/Layout/useInitialData'
import CartV3 from '@page-components/Order/CartV3'
import CartDelivery from '@page-components/Order/CartV3/CartContent/CartDelivery'
import useLayout from '@page-components/Order/useLayout'
import {useRouter} from 'next/router'
import {Suspense, useCallback, useEffect, useRef, useState} from 'react'
import AdminBar from './AdminBar'
import GoToOrderButton from './GoToOrderButton'
import DesktopNavbarLinks from './Links/DesktopNavbarLinks'
import MobileNavbarLinks from './Links/MobileNavbarLinks'
import {ORDER_PAGE, ORDER_PAGE_SERVER} from './Links/comparePaths'
import LogoLink from './LogoLink'
import NavbarSkeleton from './Skeleton'
import UserMenu from './UserMenu'
import WebsiteCoins from './WebsiteCoins'

function InnerNavbarV2() {
  const {website} = useInitialData()
  const isAdmin = useHasUserRoleInWebsite('admin')
  const navbar = website?.navbarConfiguration || {
    backgroundColor: '#fff',
    textColor: '#111',
    logo: null,
    buttonColor: null,
    links: null,
  }
  const [isScrolled, setIsScrolled] = useState(false)
  const navbarRef = useRef(null)
  const router = useRouter()
  const currentPath = getPath(router)
  const isCurrentPageOrderPage =
    currentPath.includes(ORDER_PAGE) || currentPath.includes(ORDER_PAGE_SERVER)
  const cartLayoutType = useLayout()
  const isMobileCart = cartLayoutType === 'mobile'
  const isV2Cart = cartLayoutType === 'v2'
  const navbarContainerStyle = {
    className: 'mainNavbar relative top-0 left-0 w-screen z-[250] md:pr-[15px]',
    customStyle: {backgroundColor: navbar.backgroundColor, color: navbar.textColor},
  }
  const navbarStyle =
    'mx-auto flex flex-row justify-between items-center p-4 md:p-4 max-w-[100dvw] md:max-w-[1200px]'

  const handleScroll = () => {
    const threshold = navbarRef?.current?.clientHeight
    if (!threshold) return
    if (window.scrollY > threshold) {
      setIsScrolled(true)
    } else {
      setIsScrolled(false)
    }
  }

  const listenToScroll = useCallback(() => {
    requestAnimationFrame(() => handleScroll())
  }, [handleScroll])

  useEffect(() => {
    window.addEventListener('scroll', listenToScroll)
    return () => window.removeEventListener('scroll', listenToScroll)
  }, [navbarRef])

  return (
    <>
      {isAdmin ? (
        <ClientSuspense fallback={null}>
          <AdminBar pages={website.pages} />
        </ClientSuspense>
      ) : null}
      <div
        ref={navbarRef}
        className={classnames(navbarContainerStyle.className, {
          sticky: !isMobileCart,
          'drop-shadow-sm': (!isCurrentPageOrderPage || !isMobileCart) && isScrolled,
        })}
        style={navbarContainerStyle.customStyle}
      >
        <div className={navbarStyle}>
          <div
            className="flex items-center space-x-4 md:space-x-5"
            style={{color: navbar.textColor}}
          >
            {isMobileCart && <MobileNavbarLinks links={navbar.links} />}
            <LogoLink url={navbar?.logo?.url} title={website.name} isScrolled={isScrolled} />
          </div>
          <div className="flex items-center justify-between space-x-3">
            {isCurrentPageOrderPage && isV2Cart && (
              <div className="flex-1 h-12 w-80">
                <CartDelivery />
              </div>
            )}
            {isCurrentPageOrderPage && <WebsiteCoins />}
            {(isCurrentPageOrderPage || !isMobileCart) && <UserMenu />}
            {!isCurrentPageOrderPage && <GoToOrderButton />}
            {isCurrentPageOrderPage && isV2Cart && <CartV3.AsSidebar />}
          </div>
        </div>
      </div>
      {!isMobileCart && (
        <DesktopNavbarLinks
          links={navbar.links}
          navbarCustomStyle={navbarContainerStyle.customStyle}
        />
      )}
      {isCurrentPageOrderPage && isMobileCart && (
        <div className="drop-shadow-sm" style={navbarContainerStyle.customStyle}>
          <div className={classnames(navbarStyle, 'pt-0')}>
            <div className="flex-1 h-12 w-full">
              <CartDelivery />
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default function NavbarV2() {
  return (
    <Suspense fallback={<NavbarSkeleton />}>
      <InnerNavbarV2 />
    </Suspense>
  )
}
